import React, { useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { translate } from '../../services/translate';
import MyAppText from '../MyAppText';
import MyDataTable from '../MyDataTable';
import InvolvedPersonModal from './InvolvedPersonModal';
import CustomButton from '../CustomButton';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import AlertModal from '../AlertModal';
import { TranslateRich } from '../TranslateRich';

interface InvolvedPeopleParams {
    involvedPeople: InvolvedPerson[];
    setInvolvedPeople: (value: InvolvedPerson[]) => void;
    updateOccurrenceInvolved: () => Promise<void>;
}

export default function InvolvedPeople({ involvedPeople, setInvolvedPeople, updateOccurrenceInvolved }: InvolvedPeopleParams) {
    const { styles, theme } = useStyles(styleSheet);

    const [selectedInvolvedPerson, setSelectedInvolvedPerson] = useState<InvolvedPerson>(getBlankPerson());
    const [deletePersonModalVisible, setDeletePersonModalVisible] = useState(false);
    const [personModalOpen, setPersonModalOpen] = useState<boolean>(false);

    function getBlankPerson(): InvolvedPerson {
        return {
            id: undefined,
            name: '',
            motherName: '',
            birth: '',
            documentType: 'rg',
            document: '',
            gender: 'male',
            personRelationId: 0,
            arrestWarrantNumber: '',
            publicBody: '',
            arrested: false,
            policeReport: '',
            policeReportNatureId: null,
            handcuffReasonId: 0,
            providenceId: 0,
            handcuffUsed: false,
            usedBat: false,
            usedElectricGun: false,
            usedFireGun: false,
            usedSpreaderGas: false,
            movedToFirstAidStation: false,
            movedToHospital: false,
            movedToLegalMedicalInstitute: false,
            movedToPoliceStation: false,
            address: '',
            company: '',
            companyAddress: '',
            companyNeighborhood: '',
            companyPhoneNumber: '',
            companyRole: '',
            documentSeries: '',
            documentState: '',
            neighborhood: '',
            phoneNumber: '',
        };
    }

    async function createUpdateInvolvedPeople() {
        if (!selectedInvolvedPerson.id) {
            selectedInvolvedPerson.id = -Math.random();
            involvedPeople.push(selectedInvolvedPerson);
        } else {
            const index = involvedPeople.findIndex(person => person.id === selectedInvolvedPerson.id);
            involvedPeople[index] = { ...involvedPeople[index], ...selectedInvolvedPerson };
        }

        setInvolvedPeople(involvedPeople);
        setSelectedInvolvedPerson(getBlankPerson());
    }

    return (
        <>
            <View style={{ flex: 1, rowGap: 20 }}>
                <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                    <MyAppText style={styles.headerText}>{translate('involvedPeople')}</MyAppText>
                    <CustomButton
                        onPress={() => setPersonModalOpen(true)}
                        text={translate('attach')}
                        icon={faPlus}
                    />
                </View>
                <MyDataTable
                    columns={[
                        {
                            name: translate('name'),
                            selector: row => row.name,
                            wrap: true,
                        },
                        {
                            name: translate('motherName'),
                            selector: row => row.motherName,
                            wrap: true,
                        },
                        {
                            name: translate('birth'),
                            selector: row => row.birth,
                            wrap: true,
                        },
                        {
                            name: translate('document'),
                            selector: row => row.document,
                            wrap: true,
                        },
                        {
                            name: translate('gender'),
                            selector: row => translate(row.gender),
                            wrap: true,
                        },
                        {
                            name: translate('relation'),
                            selector: row => row.personRelation?.name || '',
                            wrap: true,
                        },
                        {
                            name: translate('arrestWarrantNumber'),
                            selector: row => row.arrestWarrantNumber || '',
                            wrap: true,
                        },
                        {
                            name: translate('action'),
                            button: true,
                            cell: row =>
                                <View style={{ flexDirection: 'row', columnGap: 10 }}>
                                    <TouchableOpacity onPress={() => {
                                        setSelectedInvolvedPerson(row);
                                        setPersonModalOpen(true);
                                    }}>
                                        <FontAwesomeIcon icon={faEdit} fontSize={16} color={theme.colors.iconColor} />
                                    </TouchableOpacity>
                                    <TouchableOpacity onPress={() => {
                                        setSelectedInvolvedPerson(row);
                                        setDeletePersonModalVisible(true);
                                    }}>
                                        <FontAwesomeIcon icon={faTrashCan} fontSize={16} color={theme.colors.iconColor} />
                                    </TouchableOpacity>
                                </View>
                        }
                    ]}
                    data={involvedPeople}
                />
            </View>
            <AlertModal
                isModalVisible={deletePersonModalVisible}
                setModalVisible={setDeletePersonModalVisible}
                description={<>
                    <TranslateRich
                        i18nKey={'deleteInvolvedPersonAppAgentDescription'}
                        values={{
                            personName: <strong>{selectedInvolvedPerson.name}</strong>,
                        }}
                    />
                    <br />
                    <br />
                    <TranslateRich i18nKey={'proceedWithDeletion'} />
                </>}
                title={translate('deleteInvolvedPerson')}
                onSubmit={() => {
                    const index = involvedPeople.findIndex(person => person.id === selectedInvolvedPerson.id);
                    if (index >= 0) {
                        involvedPeople.splice(index, 1);
                        setInvolvedPeople(involvedPeople);
                        setSelectedInvolvedPerson(getBlankPerson());
                        updateOccurrenceInvolved();
                    }
                }}
                customButtonLabel={translate('yesDelete')}
                type='destructive'
            />
            <InvolvedPersonModal
                involvedPerson={selectedInvolvedPerson}
                setInvolvedPerson={setSelectedInvolvedPerson}
                isModalVisible={personModalOpen}
                setModalVisible={(value) => {
                    if (!value) {
                        setSelectedInvolvedPerson(getBlankPerson());
                    }
                    setPersonModalOpen(value);
                }}
                onSubmit={async () => {
                    await createUpdateInvolvedPeople();
                    updateOccurrenceInvolved();
                }}
            />
        </>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    headerText: {
        color: theme.colors.labelColor,
        fontSize: 16,
        fontWeight: 'bold',
    }
}));
